import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isEmpty, isString } from 'lodash';

import axios from '../../shared/axios';

import { AppDispatch, RootState } from '../../store';

import { Form, Button, Space, Tabs, Spin, Input, App } from 'antd';
import { FormInstance } from 'antd/lib/form';

import * as actions from './store/actions';

import GenerateSignature from './signature-source/generate-signature';
import UploadSignature from './signature-source/upload-signature';
import CanvasSignature from './signature-source/canvas-signature';

import withErrorHandler from '../../hoc/with-error-handler';

import './styles.less';

const signatureSourceTypes = Object.freeze({
  generate: 0,
  upload: 1,
  signatureCanvas: 2,
});

export const fieldNames = Object.freeze({
  signatureSource: 'signatureSource',
  signatureText: 'signatureText',
  signatureName: 'signatureName',
  style: 'style',
});

export type SignatureComponentProps = {
  setOnSaveCallback?: (callback: () => void) => void;
  signatureId?: string;
  setSaveLoading?: (loading: boolean) => void;
  setSaveButtonDisabled?: (disabled: boolean) => void;
  cancelCallback?: () => void;
  signatureCreateCallback?: () => void;
  truckingCompanyId?: string;
  agentId?: string;
  certificateHolderId?: string;
  insuranceAgencyId?: string;
  policyId?: string;
  isModal?: boolean;
  redirectUrl?: string;
}

const SignatureComponent: React.FC<SignatureComponentProps> = (props) => {
  const {
    setOnSaveCallback,
    signatureId,
    setSaveLoading,
    setSaveButtonDisabled,
    cancelCallback,
    signatureCreateCallback,
    truckingCompanyId,
    agentId,
    certificateHolderId,
    insuranceAgencyId,
    policyId,
    isModal,
    redirectUrl,
  } = props;

  const { notification } = App.useApp();

  const dispatch = useDispatch<AppDispatch>();

  const [signatureSource, setSignatureSource] = useState<number>(signatureSourceTypes.generate);
  const [bytesToSave, setBytesToSave] = useState<ArrayBuffer | undefined | null>(undefined);
  const [nameToSave, setNameToSave] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [canvasBytes, setCanvasBytes] = useState<ArrayBuffer | undefined>(undefined);
  const [generateBytes, setGenerateBytes] = useState<ArrayBuffer | undefined>(undefined);
  const [uploadBytes, setUploadBytes] = useState<ArrayBuffer | undefined>(undefined);

  const { saveSignatureLoading, generatedSignature, originalSignatureBytesLoading, generatedSignatureLoading }
    = useSelector((state: RootState) => state.signature);

  const formRef = useRef<FormInstance>(null);

  const latestValues = useRef({
    nameToSave: undefined as string | undefined,
    bytesToSave: undefined as ArrayBuffer | undefined | null,
  });

  useEffect(() => {
    latestValues.current = { nameToSave, bytesToSave };
  }, [nameToSave, bytesToSave]);

  useEffect(() => {
    if (typeof setOnSaveCallback === 'function') {
      setOnSaveCallback(() => onSave);
    }
  }, [setOnSaveCallback]);

  useEffect(() => {
    if (isString(signatureId) && !isEmpty(signatureId)) {
      Promise.all([
        dispatch(actions.getSignatureName(signatureId)).then((signatureName) => {
          setNameToSave(signatureName);
          setFormValues({ signatureName });
        }),
        dispatch(actions.getOriginalSignatureBytes(signatureId)).then((bytes) => {
          setBytesToSave(bytes);
        }),
      ]).then(() => {
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [signatureId]);

  useEffect(() => {
    if (typeof setSaveLoading === 'function') {
      setSaveLoading(saveSignatureLoading);
    }
  }, [saveSignatureLoading, setSaveLoading]);

  useEffect(() => {
    if (typeof setSaveButtonDisabled === 'function') {
      setSaveButtonDisabled(!bytesToSave || !nameToSave);
    }
  }, [bytesToSave, nameToSave, setSaveButtonDisabled]);

  useEffect(() => {
    setBytesToSave(generatedSignature);
  }, [generatedSignature]);

  const onCancel = (redirectUrl?: string) => {
    if (typeof cancelCallback === 'function') {
      cancelCallback();
    }

    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  const onSave = useCallback((redirectUrl?: string) => {
    if (!saveSignatureLoading) {
      formRef.current?.validateFields().then(() => {

        const { nameToSave, bytesToSave } = latestValues.current;

        dispatch(actions.saveSignature({
          signatureId,
          signatureBytes: Array.from(new Uint8Array(bytesToSave!)),
          signatureName: nameToSave,
          truckingCompanyId,
          policyId,
          agentId,
          certificateHolderId,
          insuranceAgencyId,
        })).then(() => {
          notify(`Signature was successfully ${signatureId ? 'updated' : 'saved'}`);

          if (typeof signatureCreateCallback === 'function') {
            signatureCreateCallback();
          }

          if (typeof cancelCallback === 'function') {
            cancelCallback();
          }

          if (redirectUrl) {
            window.location.href = redirectUrl;
          }
        });
      });
    }
  }, [nameToSave, bytesToSave, signatureSource, saveSignatureLoading, signatureId]);

  const notify = (description: string) => {
    notification.success({
      message: 'Success',
      top: 55,
      duration: 10,
      description,
    } as any);
  };

  const setFormValues = (value: Partial<Record<string, any>>) => {
    formRef.current?.setFieldsValue(value);
  };

  const getFormValues = (): Record<string, any> => {
    return { ...formRef.current?.getFieldsValue() };
  };

  const items = [
    {
      key: '0',
      label: 'Text Signature',
      children: (
        <GenerateSignature setBytesToSave={(bytes) => { setGenerateBytes(bytes); setBytesToSave(bytes); }} getFormValues={getFormValues} />
      ),
    },
    {
      key: '1',
      label: 'Upload Signature',
      children: <UploadSignature setBytesToSave={(bytes) => { setUploadBytes(bytes); setBytesToSave(bytes); }} />,
    },
    {
      key: '2',
      label: 'Draw Signature',
      children: <CanvasSignature setBytesToSave={(bytes) => { setCanvasBytes(bytes); setBytesToSave(bytes); }} />,
    },
  ];

  const onTabChange = (value: string) => {
    const tabKey = parseInt(value, 10);
    switch (tabKey) {
      case 0:
        setBytesToSave(generateBytes);
        setSignatureSource(tabKey);
        break;
      case 1:
        setBytesToSave(uploadBytes);
        setSignatureSource(tabKey);
        break;
      case 2:
        setBytesToSave(canvasBytes);
        setSignatureSource(tabKey);
        break;
      default:
        break;
    }
  };

  return (
    <div style={{ width: '579px', padding: '10px' }}>
      <Spin spinning={isLoading || originalSignatureBytesLoading || generatedSignatureLoading || saveSignatureLoading}>
        <Form ref={formRef} size="small">
          <Form.Item
            label="Name"
            name={fieldNames.signatureName}
            required
            rules={[
              {
                required: true,
                message: 'This field is required.',
                whitespace: true,
              },
              { max: 200, message: 'Name must be maximum 200 characters.' },
            ]}
          >
            <Input onChange={(e) => setNameToSave(e.target.value)} value={nameToSave} />
          </Form.Item>

          <Tabs defaultActiveKey="0" items={items} onChange={onTabChange} />

          {!isModal ? (
            <Form.Item>
              <Space style={{ float: 'right', marginTop: '10px' }}>
                <Button onClick={() => onCancel(redirectUrl)}>Cancel</Button>
                <Button
                  type="primary"
                  onClick={() => onSave(redirectUrl)}
                  disabled={!bytesToSave || !nameToSave}
                  loading={saveSignatureLoading}
                >
                  Save
                </Button>
              </Space>
            </Form.Item>
          ) : null}
        </Form>
      </Spin>
    </div>
  );
};

export default withErrorHandler(SignatureComponent, axios);
